/**
 * Real .xlsx export for the admin tables.
 *
 * This exists for one reason CSV cannot cover: a CSV carries no formatting at
 * all, so there is nothing in the file that can set a column width. Excel opens
 * every column at its default ~8.43 characters, which is narrower than a
 * timestamp — so a date cell renders as `###` and a text cell gets chopped off
 * at the column edge. Neither is a data problem, but both look like one, and
 * both send an admin to the phone.
 *
 * An .xlsx stores column widths, so the sheet opens already readable. It also
 * keeps timestamps as genuine date cells, so sorting, date filters and
 * `End - Start` arithmetic all still work — which forcing the values to text
 * would have thrown away.
 *
 * `exceljs` is loaded on demand rather than imported at the top of the file, so
 * it lands in its own chunk and never enters the main bundle. Nobody who does
 * not press Export pays for it.
 */

import { ReportColumn, ReportValue, formatReportDate, reportFileName, reportTotals } from '@/misc/report';

// Type-only, so it is erased at compile time and pulls nothing into the bundle.
import type { Workbook } from 'exceljs';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * How Excel should render a date cell. Matches `formatReportDate`, so the
 * spreadsheet reads the same as the CSV, the PDF and the screen.
 */
const DATE_NUMBER_FORMAT = 'yyyy-mm-dd hh:mm';

/**
 * Column widths are measured in characters of the default font. A column is
 * sized to its widest cell, then clamped: narrow enough that a short column
 * does not waste the page, wide enough that a long address is still useful
 * without pushing every other column off-screen.
 */
const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 40;
const COLUMN_PADDING = 2;

export interface XlsxReportOptions<T> {
    fileName: string,
    /** Worksheet tab name, e.g. "Rides". */
    sheetName: string,
    rows: T[],
    columns: ReportColumn<T>[]
}

/**
 * The width a value occupies once rendered. Dates are measured by their
 * formatted length rather than their raw string form, which is far longer.
 */
function displayLength(value: ReportValue): number {
    if (value === null || value === undefined) {
        return 0;
    }
    if (value instanceof Date) {
        return formatReportDate(value).length;
    }
    return String(value).length;
}

function columnWidth<T>(column: ReportColumn<T>, rows: T[]): number {
    const widest = rows.reduce(
        (longest, row) => Math.max(longest, displayLength(column.value(row))),
        column.header.length
    );
    return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, widest + COLUMN_PADDING));
}

/**
 * `undefined` leaves a genuinely empty cell, where `null` would write a blank
 * that still counts as a value to Excel's filters.
 */
function toCell(value: ReportValue): string | number | boolean | Date | undefined {
    return value === null || value === undefined ? undefined : value;
}

/**
 * Triggers a browser download of the given workbook, revoking the object URL
 * afterwards so repeated exports do not leak a blob per click.
 */
function downloadXlsx(fileName: string, buffer: ArrayBuffer | Uint8Array): void {
    const url = window.URL.createObjectURL(new Blob([buffer], { type: XLSX_MIME }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/**
 * Builds the workbook. Kept free of the DOM so it can be exercised directly
 * against real data, without a browser.
 *
 * The constructor is passed in rather than imported so the module stays
 * lazily loaded at the one call site that needs it.
 */
export function buildReportWorkbook<T>(
    WorkbookConstructor: new () => Workbook,
    options: XlsxReportOptions<T>
): Workbook {
    const workbook = new WorkbookConstructor();
    const sheet = workbook.addWorksheet(options.sheetName);

    sheet.columns = options.columns.map((column) => ({
        header: column.header,
        width: columnWidth(column, options.rows)
    }));

    options.rows.forEach((row) => {
        sheet.addRow(options.columns.map((column) => toCell(column.value(row))));
    });

    if (options.columns.some((column) => column.total)) {
        const totals = reportTotals(options.rows, options.columns);
        let labelled = false;
        const totalRow = sheet.addRow(options.columns.map((column, index) => {
            if (totals[index] !== undefined) {
                return totals[index];
            }
            if (!labelled) {
                labelled = true;
                return 'Total';
            }
            return undefined;
        }));
        totalRow.font = { bold: true };
    }

    // Applied per cell rather than per column: exceljs column styles do not
    // reach rows added positionally, and the header must stay text.
    options.columns.forEach((column, index) => {
        sheet.getColumn(index + 1).eachCell({ includeEmpty: false }, (cell) => {
            if (cell.value instanceof Date) {
                cell.numFmt = DATE_NUMBER_FORMAT;
            }
        });
    });

    const header = sheet.getRow(1);
    header.font = { bold: true };

    // The header stays put while scrolling, and every column gets a filter
    // dropdown — the two things an admin would otherwise set up by hand.
    sheet.views = [{ state: 'frozen', ySplit: 1 }];
    sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: options.columns.length }
    };

    return workbook;
}

export async function downloadTableXlsx<T>(options: XlsxReportOptions<T>): Promise<void> {
    const ExcelJS = await import(/* webpackChunkName: "exceljs" */ 'exceljs');
    const workbook = buildReportWorkbook(ExcelJS.Workbook, options);
    downloadXlsx(options.fileName, await workbook.xlsx.writeBuffer());
}

export function xlsxFileName(prefix: string, scope: string | null, from: string | null, to: string | null): string {
    return reportFileName(prefix, scope, from, to, 'xlsx');
}
